import { memo, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { useMutation } from 'react-relay';
import { verifyAuthentification } from 'lib/utils/authentication';
import { AUTH_HANDLE_VERIFY_EMAIL_MUTATION } from 'services/auth/auth.services';
import {
  authHandleVerifyUserEmailMutation,
  HandleVerifyUserEmailInput,
} from 'services/auth/__generated__/authHandleVerifyUserEmailMutation.graphql';
import { SignUpPreloadDataType } from './data';

import { v } from 'lib/utils/validation';

import { AuthPageWrapper } from 'app/pages/AuthPages/AuthPageWrapper';
import { PractitionerTypeSelect } from '../containers/PractitionerTypeSelect';
import { SignUpForm } from './containers/SignUpForm';
import MultiStepForm from 'app/components/MultiStepForm';

import { Col } from 'styles/__layout';
import { BaseText, SpanText } from 'styles/__typography';
import packageJSON from '../../../../../package.json';

import AuthBg from 'assets/images/unmind-welcome.png';

interface Props {
  preloadedData: SignUpPreloadDataType;
}

export interface FormValues {
  email: string;
  practitionerType: string;
  countryId: string;
  country?: string;
}

export const SignUpIndex = memo(({ preloadedData: { countriesQueryRef } }: Props) => {
  /**
   * State
   */
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  // check if utm_id is present in the url
  const utmId = window.location.search.split('utm_id=')[1];

  /**
   * Lifecycle
   */
  useEffect(() => {
    verifyAuthentification().then(result => {
      if (result) {
        if (result.success) window.open('/', '_self');
      }
    });
  }, []);

  /**
   * Services
   */
  const [handleVerifyUserEmail] = useMutation<authHandleVerifyUserEmailMutation>(AUTH_HANDLE_VERIFY_EMAIL_MUTATION);

  /**
   * Form
   */
  const {
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    control,
    getValues,
    setValue,
    register,
  } = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(
      Yup.object().shape({
        email: v.email,
        practitionerType: v.practitionerType,
        countryId: v.country,
      }),
    ),
    defaultValues: {
      email: '',
      countryId: '',
    },
  });

  /**
   * Functions
   */
  const onSubmit = ({ email, practitionerType, countryId, country }) => {
    setLoading(true);

    let input: HandleVerifyUserEmailInput = {
      email,
      practitionerType,
      countryId,
      type: 'CONNECT',
      utmId: utmId !== undefined ? utmId : null,
    };

    handleVerifyUserEmail({
      variables: {
        input,
      },
      onCompleted: () => {
        setLoading(false);
        setIsEmailSent(true);
      },
      onError: () => {
        setLoading(false);
        setError('Unable to create account. Please try again later.');
      },
    });
  };

  const steps = [
    <PractitionerTypeSelect
      control={control}
      title="Let’s get started!"
      subtitle="To make sure our clients are in good hands, we will ask you to provide proof of licensure and certification
    during your application."
    />,
    <SignUpForm
      countriesQueryRef={countriesQueryRef}
      setIsEmailSent={setIsEmailSent}
      isEmailSent={isEmailSent}
      onSubmit={onSubmit}
      isLoading={isLoading}
      error={error}
      register={register}
      control={control}
      getValues={getValues}
      setValue={setValue}
      setError={setError}
      errors={errors}
      handleSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isValid={isValid}
    />,
  ];

  /**
   * Return
   */
  return (
    <>
      <Helmet>
        <title>{`${packageJSON.appName} - Sign Up`}</title>
        <meta name="description" content={`${packageJSON.appName} - Sign Up`} />
      </Helmet>
      <div>
        <AuthPageWrapper bgImage={AuthBg}>
          <MultiStepForm name="signup" steps={steps} withHeader={false} saveToLocalStorage={false} />
          <Col align="center" justify="center" mt={6}>
            <BaseText textAlign="center">
              Already have an Unmind Connect account?{' '}
              <SpanText onClick={() => window.open('/login', '_self')} color="blues.2">
                Login
              </SpanText>
            </BaseText>
          </Col>
        </AuthPageWrapper>
      </div>
    </>
  );
});

export default SignUpIndex;
